'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { ArrowRight, Loader2, Check, AlertCircle } from 'lucide-react';
import { newsletterApi } from '@/lib/api';

interface NewsletterFormProps {
  placeholder: string;
  buttonText: string;
  source?: string;
  variant?: 'footer' | 'page';
}

export default function NewsletterForm({
  placeholder,
  buttonText,
  source = 'footer',
  variant = 'footer'
}: NewsletterFormProps) {
  const t = useTranslations('newsletter');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage(t('invalidEmail'));
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const response = await newsletterApi.subscribe(email, source);
      
      if (response.success) {
        setStatus('success');
        setMessage(
          response.data?.message ||
          response.message ||
          t('subscribed')
        );
        setEmail('');
        
        // Reset after 5 seconds
        setTimeout(() => {
          setStatus('idle');
          setMessage('');
        }, 5000);
      } else {
        setStatus('error');
        setMessage(t('error'));
      }
    } catch {
      setStatus('error');
      setMessage(t('error'));
    }
  };

  if (variant === 'footer') {
    return (
      <div className="w-full lg:w-auto">
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={placeholder}
            disabled={status === 'loading' || status === 'success'}
            className="px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white placeholder:text-white/50 focus:outline-none focus:border-cerad-gold w-full sm:w-80 disabled:opacity-50"
          />
          <button
            type="submit"
            disabled={status === 'loading' || status === 'success'}
            className="btn-gold whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {status === 'loading' ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {t('sending')}
              </>
            ) : status === 'success' ? (
              <>
                <Check className="w-4 h-4" />
                {t('subscribed')}
              </>
            ) : (
              <>
                {buttonText}
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </form>
        {message && (
          <p className={`mt-2 text-sm ${status === 'error' ? 'text-red-300' : 'text-green-300'}`}>
            {message}
          </p>
        )}
      </div>
    );
  }

  // Page variant (for ressources page)
  return (
    <div className="w-full max-w-md">
      <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={placeholder}
          disabled={status === 'loading' || status === 'success'}
          className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cerad-green focus:border-transparent disabled:opacity-50 disabled:bg-gray-100"
        />
        <button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          className="btn-primary whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {status === 'loading' ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : status === 'success' ? (
            <Check className="w-4 h-4" />
          ) : (
            <ArrowRight className="w-4 h-4" />
          )}
          {status === 'success' ? t('subscribed') : buttonText}
        </button>
      </form>
      {message && (
        <div className={`mt-3 flex items-center gap-2 text-sm ${status === 'error' ? 'text-red-600' : 'text-green-600'}`}>
          {status === 'error' ? <AlertCircle className="w-4 h-4" /> : <Check className="w-4 h-4" />}
          {message}
        </div>
      )}
    </div>
  );
}
